import * as React from 'react';
import { useState } from 'react';
import useSlideshows, { ActivationTag, GallerySlideshow } from '@/src/GalleryConfiguration/components/hooks/useSlideshows';
import { ConfirmDialog, Dialog, Field, SectionCard, TextInput } from '../../../components/forms';
import { Segmented } from '../../../components/table';
import { LinkRow, normalizeTag, TagEditor } from './parts';

/* Tag-scoped slideshows — each create/edit/delete saves on its own. */

interface SsForm {
    name: string;
    tags: string[];
    matchMode: 'any' | 'all';
}

export default function SlideshowsSection({
    galleryId,
    publicBase,
    rows,
    onRows,
    loadFailed,
    activationTags,
    copiedKey,
    onCopy,
}: {
    galleryId: number;
    publicBase: string;
    rows: GallerySlideshow[];
    onRows: (fn: (rows: GallerySlideshow[]) => GallerySlideshow[]) => void;
    loadFailed: boolean;
    activationTags: ActivationTag[];
    copiedKey: string | null;
    onCopy: (key: string, url: string) => void;
}) {
    const { createSlideshow, updateSlideshow, deleteSlideshow } = useSlideshows();
    /* null = closed, 0 = creating, otherwise the id being edited */
    const [editing, setEditing] = useState<number | null>(null);
    const [form, setForm] = useState<SsForm | null>(null);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [removing, setRemoving] = useState<GallerySlideshow | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const open = (s?: GallerySlideshow) => {
        setEditing(s?.id ?? 0);
        setForm({ name: s?.name ?? '', tags: s?.tags ?? [], matchMode: s?.matchMode ?? 'any' });
        setDraft('');
        setError(null);
    };
    const close = () => {
        if (busy) return;
        setEditing(null);
        setForm(null);
        setDraft('');
    };

    const save = async () => {
        if (!form || busy || editing == null) return;
        const name = form.name.trim();
        if (!name) {
            setError('Give the slideshow a name.');
            return;
        }
        /* fold an un-entered draft tag in rather than losing it */
        const d = normalizeTag(draft);
        const tags = d && !form.tags.includes(d) ? [...form.tags, d] : form.tags;
        setBusy(true);
        setError(null);
        const payload = { name, tags, matchMode: form.matchMode };
        const saved = await (editing ? updateSlideshow(editing, payload) : createSlideshow(galleryId, payload)).catch(() => null);
        setBusy(false);
        if (!saved) {
            setError('Couldn’t save the slideshow — try again.');
            return;
        }
        onRows((rs) => (editing ? rs.map((r) => (r.id === saved.id ? saved : r)) : [...rs, saved]));
        setEditing(null);
        setForm(null);
        setDraft('');
    };

    const remove = async (s: GallerySlideshow) => {
        setDeletingId(s.id);
        setError(null);
        const ok = await deleteSlideshow(s.id).catch(() => false);
        setDeletingId(null);
        if (ok) onRows((rs) => rs.filter((r) => r.id !== s.id));
        else setError(`Couldn’t delete “${s.name}” — try again.`);
    };

    return (
        <SectionCard
            title="Slideshows"
            note={rows.length ? String(rows.length) : undefined}
            action={
                <button type="button" className="tb-btn sm" onClick={() => open()} disabled={loadFailed}>
                    New slideshow
                </button>
            }
        >
            <p className="field-h gl-intro">Named slideshows scoped to a set of tags — tag photos in Media, then point a screen at a slideshow’s link.</p>
            {loadFailed ? (
                <p className="quiet-empty">Couldn’t load slideshows. Reload the page to try again.</p>
            ) : rows.length === 0 ? (
                <p className="quiet-empty">No slideshows yet — the default slideshow link shows every photo.</p>
            ) : (
                rows.map((s) => {
                    const href = publicBase ? `${publicBase}/slideshow/${s.id}` : '';
                    const key = `ss-${s.id}`;
                    return (
                        <LinkRow
                            key={s.id}
                            title={s.name}
                            href={href}
                            note={
                                s.tags.length === 0
                                    ? 'No tags — shows every photo'
                                    : `${s.tags.join(', ')}${s.tags.length > 1 ? ` · ${s.matchMode === 'all' ? 'all tags' : 'any tag'}` : ''}`
                            }
                            copied={copiedKey === key}
                            onCopy={() => onCopy(key, href)}
                            extra={
                                <>
                                    <button type="button" className="tb-btn sm" onClick={() => open(s)} aria-label={`Edit ${s.name}`}>
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="tb-btn sm gl-danger"
                                        disabled={deletingId === s.id}
                                        onClick={() => setRemoving(s)}
                                        aria-label={`Delete ${s.name}`}
                                    >
                                        {deletingId === s.id ? 'Deleting…' : 'Delete'}
                                    </button>
                                </>
                            }
                        />
                    );
                })
            )}
            {error && editing == null && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}

            <Dialog
                open={editing != null && !!form}
                title={editing ? 'Edit slideshow' : 'New slideshow'}
                onClose={close}
                onSubmit={save}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={close} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy || !form?.name.trim()}>
                            {busy ? 'Saving…' : editing ? 'Save slideshow' : 'Create slideshow'}
                        </button>
                    </>
                }
            >
                {form && (
                    <>
                        <Field label="Name">
                            <TextInput
                                autoFocus
                                placeholder="e.g. Team A screen"
                                value={form.name}
                                disabled={busy}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                            />
                        </Field>
                        <div className="field ev-gap">
                            <span className="field-l">Tags — empty shows every photo</span>
                            <TagEditor
                                label="Add a slideshow tag"
                                tags={form.tags}
                                suggestions={activationTags}
                                disabled={busy}
                                draft={draft}
                                onDraft={setDraft}
                                onChange={(tags) => setForm({ ...form, tags })}
                            />
                        </div>
                        {form.tags.length > 1 && (
                            <div className="field ev-gap">
                                <span className="field-l">Show photos matching</span>
                                <Segmented<'any' | 'all'>
                                    label="Show photos matching"
                                    value={form.matchMode}
                                    onChange={(v) => setForm({ ...form, matchMode: v })}
                                    options={[
                                        { value: 'any', label: 'Any tag' },
                                        { value: 'all', label: 'All tags' },
                                    ]}
                                />
                            </div>
                        )}
                        {error && (
                            <p className="field-e" role="alert">
                                {error}
                            </p>
                        )}
                    </>
                )}
            </Dialog>

            <ConfirmDialog
                open={!!removing}
                title="Delete slideshow?"
                body={`“${removing?.name ?? ''}” and its link stop working for any screen showing it. Photos aren’t affected.`}
                actionLabel="Delete slideshow"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />
        </SectionCard>
    );
}
